import { useEffect } from 'react'
import { useAgentStore } from '../store/agentStore.js'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'
import type { BreakCode, SessionHistory } from '../store/agentStore.js'

interface ApiSessionRecord {
  id: string
  agentId: string
  userKey: string
  userStatus: string
  userStatusTime: string
  lastStatus: string | null
  activity: string | null
  duration: number
  readyTime: number
  availableTime: number
  breakTime: number
  userState: string | null
  createdAt: string
}

export function useAgentStatus() {
  const setBreakCodes = useAgentStore((s) => s.setBreakCodes)
  const setUserStatus = useAgentStore((s) => s.setUserStatus)
  const loadSessionHistory = useAgentStore((s) => s.loadSessionHistory)

  // Validate session on mount - if Redis session was deleted server-side,
  // this will return 401 and the api client will call clearSession() + redirect.
  useEffect(() => {
    async function validateSession() {
      try {
        await api.get('/agent/break-codes')
      } catch (err) {
        // 401 is handled by api client (clearSession + redirect)
        // Other errors are logged but don't block the app
        console.error('[useAgentStatus] Session validation failed:', err)
      }
    }
    validateSession()
  }, [])

  // Load break codes on mount
  useEffect(() => {
    async function loadBreakCodes() {
      try {
        const response = await api.get<{ breakCodes: BreakCode[] }>('/agent/break-codes')
        if (response.breakCodes) {
          setBreakCodes(response.breakCodes)
        }
      } catch (err) {
        console.error('Failed to load break codes:', err)
      }
    }
    loadBreakCodes()
  }, [setBreakCodes])

  // Session history + the `immediate_available` org setting, resolved together
  // so the agent's opening status is decided exactly once.
  //
  // These used to be two effects: the history loader set "Just Logged In", and
  // auto-available raced to flip it to "Available" afterwards. Under
  // StrictMode's double-invocation the loader's deferred reset ran twice and
  // the second one landed *after* the flip, stomping it back to "Just Logged
  // In" ~30ms later. Deciding the status from both inputs before writing it
  // removes the race (and the duplicate "login" history rows it produced).
  //
  // Exactly-once comes from the `cancelled` flag alone, guarding every write.
  // Do NOT also add a ref that skips the second StrictMode invocation: the
  // first run's cleanup sets `cancelled`, so a ref that bails the second run
  // leaves *no* run able to write, and the agent silently keeps whatever
  // status the store initialised with.
  useEffect(() => {
    let cancelled = false

    void (async () => {
      const [settingsRes, historyRes] = await Promise.allSettled([
        api.get<{ immediateAvailable: boolean }>('/agent/org-settings'),
        api.get<{ records: ApiSessionRecord[] }>('/agent/session-history'),
      ])

      if (settingsRes.status === 'rejected') {
        // Non-fatal: the agent just goes Available by hand, as before.
        console.error('[useAgentStatus] immediate-available check failed:', settingsRes.reason)
      }
      const immediateAvailable =
        settingsRes.status === 'fulfilled' && settingsRes.value?.immediateAvailable === true

      if (historyRes.status === 'rejected') {
        console.error('Failed to load session history from DB:', historyRes.reason)
      }

      if (cancelled) return

      const apiRecords =
        historyRes.status === 'fulfilled' ? (historyRes.value?.records ?? []) : []

      if (apiRecords.length > 0) {
        const records: SessionHistory[] = apiRecords.map((r) => ({
          key: r.userKey,
          text: r.userStatus,
          time: new Date(r.userStatusTime),
          activity: r.activity ?? 'Status Changed',
          duration: r.duration,
          lastStatus: r.lastStatus ?? undefined,
          readyTime: r.readyTime,
          availableTime: r.availableTime,
          breakTime: r.breakTime,
        }))
        loadSessionHistory(records.reverse()) // oldest first to match store order
      }

      // The store isn't persisted, so a mount always starts on "Just Logged
      // In" — that, not the DB history, is what the agent is looking at.
      if (useAgentStore.getState().userStatus.key !== 'login') return

      if (!immediateAvailable) {
        setUserStatus({ key: 'login', text: 'Just Logged In' })
        return
      }

      // Opted in: open straight into Available so the agent doesn't have to.
      setUserStatus({ key: 'available', text: 'Available' })
      // The store change alone would leave the gateway thinking the agent is
      // paused — StatusDropdown pairs the same two, and the gateway resets any
      // non-OFFLINE agent to IDLE on the `login` AgentLayout emits, so this
      // must reach the wire after it. socket.io flushes buffered emits before
      // firing `connect` handlers, so waiting on connection orders us behind.
      const socket = getSocket()
      if (socket.connected) {
        socket.emit('agent:status', { event: 'unpause' })
      } else {
        socket.once('connect', () => {
          socket.emit('agent:status', { event: 'unpause' })
        })
      }
    })()

    return () => { cancelled = true }
  }, [loadSessionHistory, setUserStatus])

  // Sync status changes to backend
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return
      try {
        await api.post('/agent/session-history', detail)
      } catch (err) {
        console.error('Failed to sync session history to backend:', err)
      }
    }
    window.addEventListener('agent-status-changed', handler)
    return () => window.removeEventListener('agent-status-changed', handler)
  }, [])

  return {
    // Can add more agent status utilities here
  }
}
